import { useState } from "react";
import { Link, useParams } from "react-router";
import { Aviso } from "../componentes/ui";
import { CascaPublica } from "../componentes/layout";
import { FormularioDeNovaSenha } from "../componentes/acesso/FormularioDeNovaSenha";
import { ErroDaApi } from "../nucleo/api";
import { useRedefinirSenha } from "../nucleo/sessao";
import { useTitulo } from "../nucleo/titulo";

/** O link não vale mais — mesma resposta para expirado, já usado e inexistente. */
function linkPerdido(erro: unknown): boolean {
  return erro instanceof ErroDaApi && (erro.status === 404 || erro.status === 410);
}

function mensagemDaRecusa(erro: unknown): string {
  if (erro instanceof ErroDaApi) {
    // A recusa da política de senha chega com o motivo escrito para quem digita (curta,
    // vazada). Trocá-lo por uma frase genérica foi o que fez o defeito 3.5 parecer
    // "o portal recusa qualquer senha": a pessoa nunca ficava sabendo o porquê.
    if (erro.codigo === "regra_de_negocio" && erro.message) {
      return erro.message;
    }
    if (erro.status === 400 || erro.status === 422) {
      return "O portal não aceitou o envio. Confira a senha e tente de novo.";
    }
    if (erro.status === 429) {
      return "Muitas tentativas seguidas. Espere alguns minutos e tente de novo.";
    }
    if (erro.status === null) {
      return "Não foi possível falar com o portal. Verifique a conexão e tente de novo.";
    }
  }
  return "Não foi possível redefinir a senha agora. Tente de novo em instantes.";
}

function LinkInvalido() {
  return (
    <CascaPublica
      titulo="Este link não vale mais"
      descricao="Links de redefinição têm prazo curto e servem uma vez só."
      rodape={<Link to="/">Voltar para a página inicial</Link>}
    >
      <div data-testid="redefinicao-invalida">
        <Aviso tom="atencao" className="mb-4">
          <b>Nada foi alterado.</b> A sua senha atual continua valendo — se você lembrar dela,
          é só entrar.
        </Aviso>
        <div className="acesso__acoes">
          <Link to="/recuperar" className="botao">
            Pedir um link novo
          </Link>
          <Link to="/entrar" className="botao botao--fantasma">
            Ir para a entrada
          </Link>
        </div>
      </div>
    </CascaPublica>
  );
}

export function Redefinir() {
  useTitulo("Nova senha");

  const { token = "" } = useParams();
  const redefinir = useRedefinirSenha(token);
  const [erro, setErro] = useState<string | null>(null);

  /* Não existe rota para conferir um token de recuperação antes do envio, e inventar uma
     daria a quem tem o link uma forma de sondar sem consumir. O portal descobre no envio. */
  if (token.length === 0 || linkPerdido(redefinir.error)) return <LinkInvalido />;

  if (redefinir.isSuccess) {
    return (
      <CascaPublica
        titulo="Senha redefinida"
        descricao="Pronto. Agora é só entrar com a senha nova."
        rodape={<Link to="/">Voltar para a página inicial</Link>}
      >
        <div data-testid="redefinicao-concluida">
          <Aviso tom="sucesso" className="mb-4">
            Por segurança, as sessões abertas com a senha antiga deixam de valer.
          </Aviso>
          <Link to="/entrar" className="botao botao--verde botao--total">
            Entrar no portal
          </Link>
        </div>
      </CascaPublica>
    );
  }

  return (
    <CascaPublica
      titulo="Defina uma senha nova"
      descricao="Escolha uma senha que você não use em nenhum outro lugar."
      rodape={<Link to="/entrar">Voltar para a entrada</Link>}
    >
      <FormularioDeNovaSenha
        rotuloDoBotao="Salvar nova senha"
        rotuloEnviando="Salvando…"
        enviando={redefinir.isPending}
        erro={erro}
        aoEnviar={(senha) => {
          setErro(null);
          redefinir.mutate(senha, {
            onError: (causa) => {
              if (linkPerdido(causa)) return;
              setErro(mensagemDaRecusa(causa));
            },
          });
        }}
      />
    </CascaPublica>
  );
}
